import { Container, Graphics, Sprite, Texture, Application } from 'pixi.js';
import { WALL_THICKNESS } from './constants';

interface Portal {
    x: number;
    y: number;
    sprite: Sprite;
    graphics: Graphics;
    type: 'entry' | 'exit';
    pairId: number;
    rotation: number;
}

export default class PortalManager {
    private app: Application;
    private gameWorld: Container;
    private portals: Portal[] = [];
    private readonly PORTAL_RADIUS = 30;
    private readonly MAX_PORTAL_DISTANCE = 800; // Maximum distance between paired portals

    constructor(app: Application, gameWorld: Container) {
        this.app = app;
        this.gameWorld = gameWorld;
    }

    public createPortalPair(
        entryX: number,
        entryY: number,
        exitX: number,
        exitY: number,
        pairId: number
    ): void {
        // Verify portals aren't too far apart
        const distance = Math.sqrt(
            Math.pow(exitX - entryX, 2) + Math.pow(exitY - entryY, 2)
        );
        
        if (distance > this.MAX_PORTAL_DISTANCE) {
            console.warn(`Portals too far apart (${distance.toFixed(0)}px), max is ${this.MAX_PORTAL_DISTANCE}px`);
            return;
        }

        // Create entry portal (blue/purple)
        const entryPortal = this.createPortal(entryX, entryY, 'entry', pairId);
        this.portals.push(entryPortal);

        // Create exit portal (orange/yellow)
        const exitPortal = this.createPortal(exitX, exitY, 'exit', pairId);
        this.portals.push(exitPortal);
    }

    private createPortal(x: number, y: number, type: 'entry' | 'exit', pairId: number): Portal {
        const graphics = new Graphics();
        
        // Portal colors
        const outerColor = type === 'entry' ? 0x4169E1 : 0xFF8C00; // Blue for entry, orange for exit
        const innerColor = type === 'entry' ? 0x9370DB : 0xFFD700; // Purple for entry, gold for exit
        
        // Draw outer ring
        graphics.circle(0, 0, this.PORTAL_RADIUS);
        graphics.fill({ color: outerColor, alpha: 0.8 });
        
        // Draw inner ring
        graphics.circle(0, 0, this.PORTAL_RADIUS * 0.6);
        graphics.fill({ color: innerColor, alpha: 0.9 });
        
        // Draw center
        graphics.circle(0, 0, this.PORTAL_RADIUS * 0.3);
        graphics.fill({ color: 0xFFFFFF, alpha: 0.5 });
        
        // Draw swirling effect (lines)
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            const startRadius = this.PORTAL_RADIUS * 0.4;
            const endRadius = this.PORTAL_RADIUS * 0.9;
            graphics.moveTo(
                Math.cos(angle) * startRadius,
                Math.sin(angle) * startRadius
            );
            graphics.lineTo(
                Math.cos(angle) * endRadius,
                Math.sin(angle) * endRadius
            );
            graphics.stroke({ width: 2, color: 0xFFFFFF, alpha: 0.6 });
        }
        
        // Graphics are drawn centered at (0,0), so position is already correct
        graphics.x = x;
        graphics.y = y;
        
        this.gameWorld.addChild(graphics);

        return {
            x,
            y,
            sprite: null as any, // Not using sprite, using graphics
            graphics,
            type,
            pairId,
            rotation: 0
        };
    }

    public getExitPortal(entryPairId: number): Portal | null {
        return this.portals.find(p => p.type === 'exit' && p.pairId === entryPairId) || null;
    }

    public getPortalAt(x: number, y: number, radius: number = 35): Portal | null {
        return this.portals.find(portal => {
            const distance = Math.sqrt(
                Math.pow(portal.x - x, 2) + Math.pow(portal.y - y, 2)
            );
            return distance <= radius;
        }) || null;
    }

    public update(delta: number): void {
        this.portals.forEach(portal => {
            // Rotate portal for swirling effect
            portal.rotation += delta * 0.1;
            portal.graphics.rotation = portal.rotation;
            
            // Pulse effect
            const pulse = 1 + Math.sin(Date.now() * 0.005 + portal.pairId) * 0.15;
            portal.graphics.scale.set(pulse);
            
            // Glow effect
            const alpha = 0.7 + Math.sin(Date.now() * 0.008 + portal.pairId) * 0.3;
            portal.graphics.alpha = alpha;
        });
    }

    public clearAll(): void {
        this.portals.forEach(portal => {
            if (portal.graphics.parent) {
                this.gameWorld.removeChild(portal.graphics);
            }
        });
        this.portals = [];
    }

    public getPortals(): Portal[] {
        return this.portals;
    }
}

